import React, { useState, useEffect } from 'react';
import awsExports from '../aws-exports';
import './ContactModal.css';

// 宛先メールアドレスはサーバー側（POST /contact）だけが持ち、画面・コードには出さない
const API_ENDPOINT = (awsExports.aws_cloud_logic_custom || []).find((a) => a.name === 'worktimerApi')?.endpoint;

const CATEGORIES = ['料金・プランについて', 'プランのお申し込み・変更・解約', '導入のご相談', '機能について', '不具合のご報告', 'その他'];

function ContactModal({ open, onClose, defaultCategory = '料金・プランについて', defaultName = '', defaultEmail = '' }) {
  const [form, setForm] = useState({ name: '', company: '', email: '', category: defaultCategory, message: '', website: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  // 開くたびに初期化
  useEffect(() => {
    if (!open) return;
    setForm({ name: defaultName, company: '', email: defaultEmail, category: defaultCategory, message: '', website: '' });
    setSent(false);
    setError('');
  }, [open, defaultCategory, defaultName, defaultEmail]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !sending) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, sending, onClose]);

  if (!open) return null;

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setError('お名前・メールアドレス・お問い合わせ内容は必須です');
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API_ENDPOINT}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || '送信に失敗しました');
      setSent(true);
    } catch (err) {
      console.error('お問い合わせ送信エラー:', err);
      setError(err.message || '送信に失敗しました。時間をおいて再度お試しください');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="contact-overlay" onClick={() => !sending && onClose()}>
      <div className="contact-modal" role="dialog" aria-modal="true" aria-labelledby="contact-title" onClick={(e) => e.stopPropagation()}>
        <div className="contact-header">
          <h3 id="contact-title">お問い合わせ</h3>
          <button className="contact-close" onClick={onClose} disabled={sending} aria-label="閉じる">✕</button>
        </div>

        {sent ? (
          <div className="contact-done">
            <div className="contact-done-icon">✉️</div>
            <p>お問い合わせを受け付けました。<br />担当者より、ご入力いただいたメールアドレス宛にご連絡いたします。</p>
            <button className="contact-btn-primary" onClick={onClose}>閉じる</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} noValidate>
            <p className="contact-lead">料金・導入のご相談など、お気軽にお問い合わせください。</p>

            <label className="contact-label">お問い合わせ種別</label>
            <select className="contact-input" value={form.category} onChange={update('category')}>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>

            <label className="contact-label">お名前<span className="contact-required">必須</span></label>
            <input className="contact-input" type="text" value={form.name} onChange={update('name')} maxLength={100} autoComplete="name" />

            <label className="contact-label">会社名</label>
            <input className="contact-input" type="text" value={form.company} onChange={update('company')} maxLength={200} autoComplete="organization" />

            <label className="contact-label">メールアドレス<span className="contact-required">必須</span></label>
            <input className="contact-input" type="email" value={form.email} onChange={update('email')} maxLength={254} autoComplete="email" />

            <label className="contact-label">お問い合わせ内容<span className="contact-required">必須</span></label>
            <textarea className="contact-input contact-textarea" value={form.message} onChange={update('message')} maxLength={3000} rows={6} />

            {/* ボット対策（人には見えない入力欄） */}
            <input
              className="contact-hp"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              value={form.website}
              onChange={update('website')}
            />

            {error && <div className="contact-error">{error}</div>}

            <button className="contact-btn-primary" type="submit" disabled={sending}>
              {sending ? '送信中…' : '送信する'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default ContactModal;
